import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Table, List, Avatar, Tag, Button, Space, Typography, InputNumber, Modal, Select, DatePicker, Progress, Divider, Segmented } from 'antd';
import { 
  DollarOutlined,
  AppstoreOutlined,
  CloseCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  PieChartOutlined,
  LineChartOutlined,
  SettingOutlined,
  
  DatabaseOutlined,
  RollbackOutlined,
  CheckCircleOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PersistentStorage } from '../../../utils/persistentStorage';
import type { ColumnsType } from 'antd/es/table';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

const { Title, Text } = Typography;

interface InventoryDashboardPageProps {}

interface InventoryStats {
  totalProducts: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
  totalWarehouses: number;
  monthlyMovements: number;
  inboundOrders: number;
  outboundOrders: number;
  pendingTransfers: number;
  // 月度變化百分比
  totalValueChange: number;
  totalProductsChange: number;
  lowStockItemsChange: number;
  outOfStockItemsChange: number;
}

interface TopProduct {
  id: string;
  name: string;
  sku: string;
  currentStock: number;
  value: number;
  status: string;
}

interface RecentMovement {
  id: string;
  date: string;
  itemName: string;
  type: string;
  quantity: number;
  warehouse: string;
  status: string;
}

const InventoryDashboardPage: React.FC<InventoryDashboardPageProps> = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<InventoryStats>({
    totalProducts: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    totalWarehouses: 0,
    monthlyMovements: 0,
    inboundOrders: 0,
    outboundOrders: 0,
    pendingTransfers: 0,
    totalValueChange: 0,
    totalProductsChange: 0,
    lowStockItemsChange: 0,
    outOfStockItemsChange: 0,
  });
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [recentMovements, setRecentMovements] = useState<RecentMovement[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [stockMovements, setStockMovements] = useState<any[]>([]);
  const [warehousesList, setWarehousesList] = useState<any[]>([]);
  const [warehouseRankingMetric, setWarehouseRankingMetric] = useState<'count' | 'quantity'>('count');
  const [pickingLists, setPickingLists] = useState<any[]>([]);
  const [packingOrders, setPackingOrders] = useState<any[]>([]);
  const [showTopNSettings, setShowTopNSettings] = useState<boolean>(false);
  const [showOutOfStockModal, setShowOutOfStockModal] = useState<boolean>(false);
  // 已移除報表/排程/分享功能所需的狀態
  const [timeRange, setTimeRange] = useState<'days' | 'monthly' | 'yearly'>('days');
  const [customDays, setCustomDays] = useState<number>(7);
  const OUT_OF_STOCK_CARD_LIMIT = 3;
  const [rangeDates, setRangeDates] = useState<any[]>([]); // [start, end]
  // const [scheduleEmail, setScheduleEmail] = useState<string>('');
  // const [scheduleFreq, setScheduleFreq] = useState<'daily' | 'weekly' | 'monthly'>('weekly');

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    // 重新計算缺貨產品列表（依據其他頁面狀態）
    if (products.length > 0) {
      const topProductsData = products
        .filter((product: any) => product.status === 'out_of_stock')
        .map((product: any) => ({
          id: product.id,
          name: product.name,
          sku: product.sku,
          currentStock: product.currentStock || 0,
          value: (product.currentStock || 0) * (product.unitPrice || 0),
          status: product.status,
        }))
        .sort((a: any, b: any) => b.value - a.value);
      setTopProducts(topProductsData);
    }
  }, [products]);

  // 當時間範圍或自定義天數改變時，圖表會自動重新渲染
  // 因為 generateTrendChartData() 函數會根據這些狀態重新計算數據

  // 計算月度變化百分比
  const calculateMonthlyChange = (currentValue: number, previousValue: number): number => {
    if (previousValue === 0) {
      return currentValue > 0 ? 100 : 0;
    }
    return ((currentValue - previousValue) / previousValue) * 100;
  };

  // 獲取上個月的模擬數據（用於比較）
  const getPreviousMonthData = () => {
    // 模擬上個月的數據，實際應用中應該從歷史數據中獲取
    return {
      totalProducts: 2, // 上個月有2個產品
      totalValue: 2000, // 上個月庫存總值
      lowStockItems: 1, // 上個月有1個低庫存產品
      outOfStockItems: 2, // 上個月有2個缺貨產品
    };
  };

  // 生成庫存移動趨勢線圖數據
  const generateTrendChartData = () => {
    if (stockMovements.length === 0) return null;

    // 先依日期區間篩選
    const filtered = (rangeDates && rangeDates.length === 2)
      ? stockMovements.filter((m: any) => {
          const t = dayjs(m.timestamp);
          return t.isAfter(rangeDates[0].startOf('day')) && t.isBefore(rangeDates[1].endOf('day'));
        })
      : stockMovements;

    let groupedData: { [key: string]: { in: number; out: number } } = {};
    let labels: string[] = [];

    if (timeRange === 'days') {
      // 按天分組
      filtered.forEach((movement: any) => {
        const date = new Date(movement.timestamp).toLocaleDateString();
        if (!groupedData[date]) {
          groupedData[date] = { in: 0, out: 0 };
        }
        if (movement.type === 'in') {
          groupedData[date].in += Math.abs(movement.quantity);
        } else if (movement.type === 'out') {
          groupedData[date].out += Math.abs(movement.quantity);
        }
      });
      
      const sortedDates = Object.keys(groupedData).sort();
      labels = sortedDates.slice(-customDays);
    } else if (timeRange === 'monthly') {
      // 按月分組
      filtered.forEach((movement: any) => {
        const date = new Date(movement.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!groupedData[monthKey]) {
          groupedData[monthKey] = { in: 0, out: 0 };
        }
        if (movement.type === 'in') {
          groupedData[monthKey].in += Math.abs(movement.quantity);
        } else if (movement.type === 'out') {
          groupedData[monthKey].out += Math.abs(movement.quantity);
        }
      });
      
      const sortedMonths = Object.keys(groupedData).sort();
      labels = sortedMonths.slice(-12); // 最近12個月
    } else if (timeRange === 'yearly') {
      // 按年分組
      filtered.forEach((movement: any) => {
        const date = new Date(movement.timestamp);
        const yearKey = date.getFullYear().toString();
        if (!groupedData[yearKey]) {
          groupedData[yearKey] = { in: 0, out: 0 };
        }
        if (movement.type === 'in') {
          groupedData[yearKey].in += Math.abs(movement.quantity);
        } else if (movement.type === 'out') {
          groupedData[yearKey].out += Math.abs(movement.quantity);
        }
      });
      
      const sortedYears = Object.keys(groupedData).sort();
      labels = sortedYears.slice(-5); // 最近5年
    }

    const inData = labels.map(label => groupedData[label]?.in || 0);
    const outData = labels.map(label => groupedData[label]?.out || 0);

    return {
      labels,
      datasets: [
        {
          label: '入庫數量',
          data: inData,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          tension: 0.4,
          fill: true,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: 'rgb(34, 197, 94)',
        },
        {
          label: '出庫數量',
          data: outData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          tension: 0.4,
          fill: true,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: 'rgb(239, 68, 68)',
        }
      ]
    };
  };

  // 匯出 CSV 報表（依日期區間）
  // 已移除分享功能

  // 退款金額（從 Return Management 的本地存儲或模擬資料）
  const totalRefundAmount = useMemo(() => {
    // 優先從 PersistentStorage 讀取 returnOrders，如果不存在，回傳 0
    const saved: any[] =
      (PersistentStorage.get('return_orders', []) as any[]) ||
      (PersistentStorage.get('sales_return_orders', []) as any[]) || [];
    const filtered = (rangeDates && (rangeDates as any).length === 2)
      ? saved.filter((r: any) => {
          const ts = r.returnDate || r.createdAt || r.updatedAt;
          if (!ts) return false;
          const t = dayjs(ts);
          return t.isAfter(rangeDates[0].startOf('day')) && t.isBefore(rangeDates[1].endOf('day'));
        })
      : saved;
    return filtered.reduce((sum: number, r: any) => sum + Number(r?.totalRefundAmount || 0), 0);
  }, [rangeDates]);

  // 區間與上期區間界線
  const currentRange = useMemo(() => {
    if (rangeDates && (rangeDates as any).length === 2) {
      return { start: rangeDates[0].startOf('day'), end: rangeDates[1].endOf('day') };
    }
    const end = dayjs().endOf('day');
    const start = dayjs().subtract(29, 'day').startOf('day');
    return { start, end };
  }, [rangeDates]);

  const previousRange = useMemo(() => {
    const days = currentRange.end.diff(currentRange.start, 'day') + 1;
    const end = currentRange.start.subtract(1, 'day').endOf('day');
    const start = end.subtract(days - 1, 'day').startOf('day');
    return { start, end };
  }, [currentRange]);

  // 區間與上期：入/出庫
  const periodInboundPrev = useMemo(() => {
    const filtered = stockMovements.filter((m: any) => {
      const t = dayjs(m.timestamp);
      return t.isAfter(previousRange.start) && t.isBefore(previousRange.end) && m.type === 'in';
    });
    return filtered.reduce((sum: number, m: any) => sum + Math.abs(m.quantity || 0), 0);
  }, [stockMovements, previousRange]);

  const periodOutboundPrev = useMemo(() => {
    const filtered = stockMovements.filter((m: any) => {
      const t = dayjs(m.timestamp);
      return t.isAfter(previousRange.start) && t.isBefore(previousRange.end) && m.type === 'out';
    });
    return filtered.reduce((sum: number, m: any) => sum + Math.abs(m.quantity || 0), 0);
  }, [stockMovements, previousRange]);

  // 區間與上期：Picking / Packing / Refund
  const pickingCompletedCurrent = useMemo(() => {
    const src = rangeDates && (rangeDates as any).length === 2 ? pickingLists.filter((x: any) => dayjs(x.createdAt || x.scheduledDate).isAfter(currentRange.start) && dayjs(x.createdAt || x.scheduledDate).isBefore(currentRange.end)) : pickingLists;
    return src.filter(p => p.status === 'completed').length;
  }, [pickingLists, currentRange, rangeDates]);

  const pickingCompletedPrev = useMemo(() => {
    const src = pickingLists.filter((x: any) => dayjs(x.createdAt || x.scheduledDate).isAfter(previousRange.start) && dayjs(x.createdAt || x.scheduledDate).isBefore(previousRange.end));
    return src.filter(p => p.status === 'completed').length;
  }, [pickingLists, previousRange]);

  const packingDiscrepanciesCurrent = useMemo(() => {
    const src = rangeDates && (rangeDates as any).length === 2 ? packingOrders.filter((x: any) => dayjs(x.createdAt).isAfter(currentRange.start) && dayjs(x.createdAt).isBefore(currentRange.end)) : packingOrders;
    return src.reduce((sum: number, p: any) => sum + (Array.isArray(p.discrepancies) ? p.discrepancies.length : 0), 0);
  }, [packingOrders, currentRange, rangeDates]);

  const packingDiscrepanciesPrev = useMemo(() => {
    const src = packingOrders.filter((x: any) => dayjs(x.createdAt).isAfter(previousRange.start) && dayjs(x.createdAt).isBefore(previousRange.end));
    return src.reduce((sum: number, p: any) => sum + (Array.isArray(p.discrepancies) ? p.discrepancies.length : 0), 0);
  }, [packingOrders, previousRange]);

  const refundPrevAmount = useMemo(() => {
    const saved: any[] =
      (PersistentStorage.get('return_orders', []) as any[]) ||
      (PersistentStorage.get('sales_return_orders', []) as any[]) || [];
    const prev = saved.filter((r: any) => {
      const ts = r.returnDate || r.createdAt || r.updatedAt;
      if (!ts) return false;
      const t = dayjs(ts);
      return t.isAfter(previousRange.start) && t.isBefore(previousRange.end);
    });
    return prev.reduce((sum: number, r: any) => sum + Number(r?.totalRefundAmount || 0), 0);
  }, [previousRange]);

  // 依日期區間末日估算當月末庫存總值與上月末庫存總值（基於現有庫存回推移動）
  const getInventoryValueAt = (date: dayjs.Dayjs): number => {
    if (!products || !stockMovements) return 0;
    const stockMap: Record<string, number> = {};
    const priceMap: Record<string, number> = {};
    (products as any[]).forEach((p: any) => {
      stockMap[p.id] = Number(p.currentStock || 0);
      priceMap[p.id] = Number(p.unitPrice || 0);
    });
    (stockMovements as any[]).forEach((m: any) => {
      const t = dayjs(m.timestamp);
      if (t.isAfter(date)) {
        const q = Math.abs(Number(m.quantity || 0));
        if (m.type === 'in') {
          stockMap[m.productId] = (stockMap[m.productId] || 0) - q;
        } else if (m.type === 'out') {
          stockMap[m.productId] = (stockMap[m.productId] || 0) + q;
        }
      }
    });
    const total = Object.keys(stockMap).reduce((sum, pid) => sum + (stockMap[pid] * (priceMap[pid] || 0)), 0);
    return Math.max(total, 0);
  };

  const inventoryValueCurrentEnd = useMemo(() => getInventoryValueAt(currentRange.end), [products, stockMovements, currentRange]);
  const inventoryValuePrevEnd = useMemo(() => getInventoryValueAt(previousRange.end), [products, stockMovements, previousRange]);
  const inventoryValueChangePct = useMemo(() => calculateMonthlyChange(inventoryValueCurrentEnd, inventoryValuePrevEnd), [inventoryValueCurrentEnd, inventoryValuePrevEnd]);

  // 依日期區間過濾後的移動記錄
  const rangeFilteredMovements = useMemo(() => {
    if (!stockMovements) return [] as any[];
    if (rangeDates && (rangeDates as any).length === 2) {
      return stockMovements.filter((m: any) => {
        const t = dayjs(m.timestamp);
        return t.isAfter(rangeDates[0].startOf('day')) && t.isBefore(rangeDates[1].endOf('day'));
      });
    }
    return stockMovements;
  }, [stockMovements, rangeDates]);

  // 期間入庫 / 出庫 總量
  const periodInboundTotal = useMemo(() => {
    return rangeFilteredMovements
      .filter((m: any) => m.type === 'in')
      .reduce((sum: number, m: any) => sum + Math.abs(m.quantity || 0), 0);
  }, [rangeFilteredMovements]);

  const periodOutboundTotal = useMemo(() => {
    return rangeFilteredMovements
      .filter((m: any) => m.type === 'out')
      .reduce((sum: number, m: any) => sum + Math.abs(m.quantity || 0), 0);
  }, [rangeFilteredMovements]);

  // 生成庫存結構餅圖數據
  const generatePieChartData = () => {
    if (products.length === 0) return null;

    const statusCounts = {
      available: products.filter((p: any) => p.status === 'available').length,
      low_stock: products.filter((p: any) => p.status === 'low_stock').length,
      out_of_stock: products.filter((p: any) => p.status === 'out_of_stock').length,
    };

    return {
      labels: ['正常庫存', '低庫存', '缺貨'],
      datasets: [
        {
          label: '產品數量',
          data: [statusCounts.available, statusCounts.low_stock, statusCounts.out_of_stock],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)',
          ],
          borderColor: [
            'rgba(34, 197, 94, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(239, 68, 68, 1)',
          ],
          borderWidth: 2,
        }
      ]
    };
  };

  const loadDashboardData = () => {
    setLoading(true);
    try {
      // 初始化 PersistentStorage
      PersistentStorage.init();
      
      // 僅讀取已存在於 Inventory 各頁面寫入的真實資料；若無資料則顯示空態，不再在儀表板注入模擬資料
      const productsData = PersistentStorage.get('products', []);
      const warehouses = PersistentStorage.get('warehouses', []);
      const stockMovementsData = PersistentStorage.get('stockMovements', []);

      // Picking / Packing / Returns 同樣僅讀取，不注入模擬
      setPickingLists(PersistentStorage.get('picking_lists', []) || []);
      setPackingOrders(PersistentStorage.get('packing_orders', []) || []);
      
      // 設置狀態
      setProducts(productsData);
      setStockMovements(stockMovementsData);
      setWarehousesList(warehouses);
      
      // 調試信息
      console.log('Dashboard Data Loaded:', {
        products: productsData.length,
        warehouses: warehouses.length,
        stockMovements: stockMovementsData.length
      });
      
      // 计算基础统计
      const totalProducts = productsData.length;
      const totalValue = productsData.reduce((sum: number, product: any) => 
        sum + (product.currentStock * (product.unitPrice || 0)), 0);
      const lowStockItems = productsData.filter((p: any) => p.status === 'low_stock').length;
      const outOfStockItems = productsData.filter((p: any) => p.status === 'out_of_stock').length;
      const totalWarehouses = warehouses.length;

      // 獲取上個月數據並計算變化
      const previousMonthData = getPreviousMonthData();
      const totalValueChange = calculateMonthlyChange(totalValue, previousMonthData.totalValue);
      const totalProductsChange = calculateMonthlyChange(totalProducts, previousMonthData.totalProducts);
      const lowStockItemsChange = calculateMonthlyChange(lowStockItems, previousMonthData.lowStockItems);
      const outOfStockItemsChange = calculateMonthlyChange(outOfStockItems, previousMonthData.outOfStockItems);
      
      // 计算月度移动
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentMovements = stockMovementsData.filter((movement: any) => {
        const movementDate = new Date(movement.timestamp);
        return movementDate >= thirtyDaysAgo;
      });
      const monthlyMovements = recentMovements.length;
      
      // 计算订单统计
      const inboundOrders = stockMovementsData.filter((m: any) => m.type === 'in').length;
      const outboundOrders = stockMovementsData.filter((m: any) => m.type === 'out').length;
      const pendingTransfers = stockMovementsData.filter((m: any) => m.status === 'pending').length;
      
      const finalStats = {
        totalProducts,
        totalValue,
        lowStockItems,
        outOfStockItems,
        totalWarehouses,
        monthlyMovements,
        inboundOrders,
        outboundOrders,
        pendingTransfers,
        totalValueChange,
        totalProductsChange,
        lowStockItemsChange,
        outOfStockItemsChange,
      };

      console.log('Final Stats:', finalStats);
      setStats(finalStats);

      // 僅顯示其他頁面已計算為「缺貨」的產品
      const topProductsData = productsData
        .filter((product: any) => product.status === 'out_of_stock')
        .map((product: any) => ({
          id: product.id,
          name: product.name,
          sku: product.sku,
          currentStock: product.currentStock || 0,
          value: (product.currentStock || 0) * (product.unitPrice || 0),
          status: product.status,
        }))
        .sort((a: any, b: any) => b.value - a.value);
      
      setTopProducts(topProductsData);

      // 获取最近的库存移动
      const recentMovementsData = ((rangeDates && (rangeDates as any).length === 2)
        ? stockMovementsData.filter((m: any) => {
            const t = dayjs(m.timestamp);
            return t.isAfter(rangeDates[0].startOf('day')) && t.isBefore(rangeDates[1].endOf('day'));
          })
        : stockMovementsData)
        .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10)
        .map((movement: any) => {
          const product = productsData.find((p: any) => p.id === movement.productId);
          return {
            id: movement.id,
            date: new Date(movement.timestamp).toLocaleDateString(),
            itemName: (product as any)?.name || movement.productName,
            type: movement.type === 'in' ? '入庫' : movement.type === 'out' ? '出庫' : '調整',
            quantity: movement.quantity,
            warehouse: movement.warehouse || '主倉庫',
            status: movement.status || '已完成',
          };
        });
      
      setRecentMovements(recentMovementsData);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const movementColumns: ColumnsType<RecentMovement> = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: '產品',
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === '入庫' ? 'green' : type === '出庫' ? 'red' : 'blue'}>
          {type}
        </Tag>
      ),
    },
    {
      title: '數量',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => (
        <span style={{ color: quantity > 0 ? '#52c41a' : '#ff4d4f' }}>
          {quantity > 0 ? '+' : ''}{quantity}
        </span>
      ),
    },
    {
      title: '倉庫',
      dataIndex: 'warehouse',
      key: 'warehouse',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === '已完成' ? 'green' : 'orange'}>
          {status}
        </Tag>
      ),
    },
  ];

  // 缺貨產品表格欄位
  const outOfStockColumns: ColumnsType<TopProduct> = [
    { title: '產品', dataIndex: 'name', key: 'name' },
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    { title: '庫存', dataIndex: 'currentStock', key: 'currentStock' },
    {
      title: '庫存價值',
      dataIndex: 'value',
      key: 'value',
      render: (v: number) => `$${(v || 0).toFixed(2)}`,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => (
        <Tag color={s === 'available' ? 'green' : s === 'low_stock' ? 'orange' : 'red'}>
          {s === 'available' ? '正常' : s === 'low_stock' ? '低庫存' : '缺貨'}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div>Loading inventory dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header with range and actions */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>庫存概覽</Title>
          <Text type="secondary">以庫存頁面資料為來源，支持日期區間分析與報表</Text>
        </div>
        <Space>
          <DatePicker.RangePicker
            value={rangeDates as any}
            onChange={(v) => setRangeDates(v as any)}
            allowClear
          />
        </Space>
      </div>


      {/* Key Performance Indicators */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="庫存總值"
              value={inventoryValueCurrentEnd}
              precision={2}
              prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
              suffix={
                <span style={{ fontSize: 12, color: inventoryValueChangePct >= 0 ? '#52c41a' : '#ff4d4f' }}>
                  {inventoryValueChangePct >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} 
                  {inventoryValueChangePct >= 0 ? '+' : ''}{inventoryValueChangePct.toFixed(1)}% vs 上月
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            {(() => {
              const totals = warehousesList.reduce((acc: any, w: any) => {
                const total = w.capacity?.total || 0;
                const used = w.capacity?.used || 0;
                return { total: acc.total + total, used: acc.used + used, unit: w.capacity?.unit || acc.unit };
              }, { total: 0, used: 0, unit: 'cbm' });
              const rate = totals.total === 0 ? 0 : Math.round((totals.used / totals.total) * 100);
              return (
                <Statistic
                  title="整體容量使用"
                  value={rate}
                  suffix="%"
                  prefix={<DatabaseOutlined style={{ color: '#fa8c16' }} />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              );
            })()}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="期間入庫總量"
              value={periodInboundTotal}
              prefix={<DownloadOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
              suffix={
                <span style={{ fontSize: 12, color: periodInboundTotal >= periodInboundPrev ? '#52c41a' : '#ff4d4f' }}>
                  {periodInboundTotal >= periodInboundPrev ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {periodInboundPrev === 0 ? (periodInboundTotal > 0 ? '+100%' : '0%') : `${(((periodInboundTotal - periodInboundPrev) / periodInboundPrev) * 100).toFixed(1)}%`} vs 上月
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="期間出庫總量"
              value={periodOutboundTotal}
              prefix={<UploadOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
              suffix={
                <span style={{ fontSize: 12, color: periodOutboundTotal >= periodOutboundPrev ? '#52c41a' : '#ff4d4f' }}>
                  {periodOutboundTotal >= periodOutboundPrev ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {periodOutboundPrev === 0 ? (periodOutboundTotal > 0 ? '+100%' : '0%') : `${(((periodOutboundTotal - periodOutboundPrev) / periodOutboundPrev) * 100).toFixed(1)}%`} vs 上月
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            {(() => {
              const rangeFilter = (arr: any[]) => (rangeDates && (rangeDates as any).length === 2)
                ? arr.filter((x: any) => dayjs(x.createdAt || x.scheduledDate).isAfter(rangeDates[0].startOf('day')) && dayjs(x.createdAt || x.scheduledDate).isBefore(rangeDates[1].endOf('day')))
                : arr;
              const pickings = rangeFilter(pickingLists);
              const completed = pickings.filter(p => p.status === 'completed').length;
              const processing = pickings.filter(p => p.status !== 'completed').length;
              return (
                <Statistic
                  title="揀貨完成/進行中"
                  value={`${completed}/${processing}`}
                  prefix={<CheckCircleOutlined style={{ color: '#13c2c2' }} />}
                  valueStyle={{ color: '#13c2c2' }}
                  suffix={
                    <span style={{ fontSize: 12, color: pickingCompletedCurrent >= pickingCompletedPrev ? '#52c41a' : '#ff4d4f' }}>
                      {pickingCompletedCurrent >= pickingCompletedPrev ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {pickingCompletedPrev === 0 ? (pickingCompletedCurrent > 0 ? '+100%' : '0%') : `${(((pickingCompletedCurrent - pickingCompletedPrev) / pickingCompletedPrev) * 100).toFixed(1)}%`} vs 上月
                    </span>
                  }
                />
              );
            })()}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            {(() => {
              const rangeFilter = (arr: any[]) => (rangeDates && (rangeDates as any).length === 2)
                ? arr.filter((x: any) => dayjs(x.createdAt).isAfter(rangeDates[0].startOf('day')) && dayjs(x.createdAt).isBefore(rangeDates[1].endOf('day')))
                : arr;
              const packings = rangeFilter(packingOrders);
              const inDiscrepancies = packings.reduce((sum: number, p: any) => sum + (Array.isArray(p.discrepancies) ? p.discrepancies.length : 0), 0);
              return (
                <Statistic
                  title="包裝差異數"
                  value={inDiscrepancies}
                  prefix={<GiftOutlined style={{ color: '#fa541c' }} />}
                  valueStyle={{ color: '#fa541c' }}
                  suffix={
                    <span style={{ fontSize: 12, color: packingDiscrepanciesCurrent <= packingDiscrepanciesPrev ? '#52c41a' : '#ff4d4f' }}>
                      {packingDiscrepanciesCurrent <= packingDiscrepanciesPrev ? <ArrowDownOutlined /> : <ArrowUpOutlined />} {packingDiscrepanciesPrev === 0 ? (packingDiscrepanciesCurrent > 0 ? '+100%' : '0%') : `${(((packingDiscrepanciesCurrent - packingDiscrepanciesPrev) / packingDiscrepanciesPrev) * 100).toFixed(1)}%`} vs 上月
                    </span>
                  }
                />
              );
            })()}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="整體退款金額"
              value={totalRefundAmount}
              precision={2}
              prefix={<RollbackOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
              suffix={
                <span style={{ fontSize: 12, color: totalRefundAmount >= refundPrevAmount ? '#52c41a' : '#ff4d4f' }}>
                  {totalRefundAmount >= refundPrevAmount ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {refundPrevAmount === 0 ? (totalRefundAmount > 0 ? '+100%' : '0%') : `${(((totalRefundAmount - refundPrevAmount) / refundPrevAmount) * 100).toFixed(1)}%`} vs 上月
                </span>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Charts and Analytics */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* 左上：庫存移動趨勢分析 */}
        <Col xs={24} lg={16}>
          <Card 
            title="庫存移動趨勢分析" 
            extra={
              <Space>
                <Button 
                  type="text" 
                  icon={<SettingOutlined />}
                  onClick={() => setShowTopNSettings(true)}
                >
                  設置
                </Button>
                <LineChartOutlined />
              </Space>
            }
          >
            <div style={{ height: 300 }}>
              {stockMovements.length > 0 ? (
                <Line 
                  data={generateTrendChartData() || { labels: [], datasets: [] }} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: { usePointStyle: true, pointStyle: 'rect', padding: 16 }
                      },
                      tooltip: {
                        mode: 'index' as const,
                        intersect: false,
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                        title: {
                          display: true,
                          text: timeRange === 'days' ? '日期' : timeRange === 'monthly' ? '月份' : '年份'
                        }
                      },
                      y: {
                        display: true,
                        title: {
                          display: true,
                          text: '數量'
                        },
                        grid: { color: 'rgba(0,0,0,0.05)' }
                      }
                    },
                    interaction: {
                      mode: 'nearest' as const,
                      axis: 'x' as const,
                      intersect: false,
                    },
                  }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <LineChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    <div style={{ marginTop: 16, color: '#999' }}>
                      暫無移動數據
                      <br />
                      <Text type="secondary">請在其他頁面添加庫存移動記錄</Text>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
        {/* 右上：缺貨產品 */}
        <Col xs={24} lg={8}>
          <Card 
            title={`缺貨產品（顯示前 ${OUT_OF_STOCK_CARD_LIMIT} 筆）`} 
            extra={<Button type="link" icon={<EyeOutlined />} onClick={() => setShowOutOfStockModal(true)}>查看全部</Button>}
          >
            {topProducts.length > 0 ? (
              <List
                dataSource={topProducts.slice(0, OUT_OF_STOCK_CARD_LIMIT)}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<AppstoreOutlined />} />}
                      title={item.name}
                      description={`SKU: ${item.sku} | 庫存: ${item.currentStock}`}
                    />
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        ${item.value.toFixed(2)}
                      </div>
                      <Tag color={item.status === 'available' ? 'green' : item.status === 'low_stock' ? 'orange' : 'red'}>
                        {item.status === 'available' ? '正常' : item.status === 'low_stock' ? '低庫存' : '缺貨'}
                      </Tag>
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                <CloseCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>暫無缺貨產品</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Bottom Panels */}
      <Row gutter={[24, 24]}>
        {/* 左下：庫存結構與容量（若餅圖已足夠說明清楚，可以只保留餅圖，不再重複顯示「產品總數 / 低庫存 / 缺貨」的KPI） */}
        <Col xs={24} lg={12}>
          <Card title="庫存結構與容量" extra={<PieChartOutlined />}>
            <div style={{ height: 300 }}>
              {products.length > 0 ? (
                <Pie 
                  data={generatePieChartData() || { labels: [], datasets: [] }} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '60%',
                    plugins: {
                      legend: {
                        position: 'right' as const,
                        labels: { padding: 20, usePointStyle: true, pointStyle: 'circle' }
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context: any) {
                            const label = context.label || '';
                            const value = context.raw || 0;
                            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                            return `${label}: ${value} 個產品 (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <PieChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    <div style={{ marginTop: 16, color: '#999' }}>
                      暫無產品數據
                      <br />
                      <Text type="secondary">請在其他頁面添加產品信息</Text>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
        {/* 右下：庫存分析（作業視角，與日期區間相關）*/}
        <Col xs={24} lg={12}>
          <Card title="庫存分析">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong>期間入/出庫占比</Text>
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary">入庫</Text>
                    <Progress percent={periodInboundTotal + periodOutboundTotal === 0 ? 0 : Math.round((periodInboundTotal / (periodInboundTotal + periodOutboundTotal)) * 100)} strokeColor="#52c41a" showInfo />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary">出庫</Text>
                    <Progress percent={periodInboundTotal + periodOutboundTotal === 0 ? 0 : Math.round((periodOutboundTotal / (periodInboundTotal + periodOutboundTotal)) * 100)} strokeColor="#ff4d4f" showInfo />
                  </div>
                </div>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <div>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text strong>活躍倉庫 Top 3</Text>
                  <Segmented
                    size="small"
                    value={warehouseRankingMetric}
                    onChange={(v) => setWarehouseRankingMetric(v as any)}
                    options={[{ label: '次數', value: 'count' }, { label: '數量', value: 'quantity' }]}
                  />
                </Space>
                <List
                  dataSource={[...warehousesList]
                    .map((w: any) => {
                      const base = (rangeDates && (rangeDates as any).length === 2)
                        ? stockMovements.filter((m: any) => {
                            const t = dayjs(m.timestamp);
                            return t.isAfter(rangeDates[0].startOf('day')) && t.isBefore(rangeDates[1].endOf('day'));
                          })
                        : stockMovements;
                      const moves = base.filter((m: any) => m.warehouse === w.name);
                      const movesCount = moves.length;
                      const quantitySum = moves.reduce((s: number, m: any) => s + Math.abs(m.quantity || 0), 0);
                      return { ...w, movesCount, quantitySum };
                    })
                    .sort((a: any, b: any) => (warehouseRankingMetric === 'count' ? b.movesCount - a.movesCount : b.quantitySum - a.quantitySum))
                    .slice(0, 3)}
                  renderItem={(w: any) => (
                    <List.Item>
                      <List.Item.Meta title={w.name} description={`位置: ${w.location || '—'}`} />
                      {warehouseRankingMetric === 'count' ? (
                        <Tag color="blue">{w.movesCount} 次移動</Tag>
                      ) : (
                        <Tag color="purple">{w.quantitySum} 件</Tag>
                      )}
                    </List.Item>
                  )}
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recent Movements Table */}
      <Card title="最近庫存移動" style={{ marginTop: 24 }}>
        <Table
          columns={movementColumns}
          dataSource={recentMovements}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      {/* Chart Settings Modal */}
      <Modal
        title="圖表設置"
        open={showTopNSettings}
        onOk={() => setShowTopNSettings(false)}
        onCancel={() => setShowTopNSettings(false)}
        okText="確定"
        cancelText="取消"
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 24 }}>
            <Text strong>趨勢圖時間範圍：</Text>
            <div style={{ marginTop: 16 }}>
              <Select
                value={timeRange}
                onChange={(value) => setTimeRange(value)}
                style={{ width: '100%' }}
                options={[
                  { value: 'days', label: '按天顯示' },
                  { value: 'monthly', label: '按月顯示' },
                  { value: 'yearly', label: '按年顯示' },
                ]}
              />
            </div>
            {timeRange === 'days' && (
              <div style={{ marginTop: 16 }}>
                <Text strong>顯示天數：</Text>
                <div style={{ marginTop: 8 }}>
                  <InputNumber
                    min={1}
                    max={90}
                    value={customDays}
                    onChange={(value) => setCustomDays(value || 7)}
                    style={{ width: '100%' }}
                    addonAfter="天"
                  />
                </div>
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  選擇顯示最近多少天的數據（1-90天）
                </div>
              </div>
            )}
            {timeRange === 'monthly' && (
              <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                顯示最近12個月的數據
              </div>
            )}
            {timeRange === 'yearly' && (
              <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                顯示最近5年的數據
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Out-of-stock Full List Modal */}
      <Modal
        title="全部缺貨產品"
        open={showOutOfStockModal}
        onOk={() => setShowOutOfStockModal(false)}
        onCancel={() => setShowOutOfStockModal(false)}
        okText="關閉"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={720}
      >
        <Table
          columns={outOfStockColumns}
          dataSource={topProducts}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Modal>

      {/* 已移除：產生報表/排程報表/分享分析功能 */}
    </div>
  );
};

export default InventoryDashboardPage;